import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { obtenerReservasUsuario, calificarReserva } from '../api/reservas';
import { useNotify } from '../components/NotificationProvider';
import { extractHHmm, to12hWithAmPm } from '../utils/time';

// Convierte 'YYYY-MM-DD' o ISO 'YYYY-MM-DDTHH:mm:ssZ' a 'DD/MM/YYYY' sin aplicar zona horaria
function toDDMMYYYY(value) {
  if (!value) return '—';
  const s = String(value);
  const ymd = s.includes('T') ? s.split('T')[0] : s; // 'YYYY-MM-DD'
  const [y, m, d] = (ymd || '').split('-');
  if (!y || !m || !d) return ymd || '—';
  return `${d}/${m}/${y}`;
}

const ReservasUsuarios = ({ user }) => {
  const [reservas, setReservas] = useState([]);
  const PAGE_SIZE = 5;
  const [page, setPage] = useState(1);
  const [mensajeSel, setMensajeSel] = useState(null);
  const [notaSel, setNotaSel] = useState(null);
  const navigate = useNavigate();
  const { error: notifyError, success: notifySuccess } = useNotify(); // Hook en el nivel superior del componente
  const [ratingReservaSel, setRatingReservaSel] = useState(null);
  const [ratingStars, setRatingStars] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [savingRating, setSavingRating] = useState(false);

  useEffect(() => {
    const cargarReservas = async () => {
      try {
        if (user && user.id) {
          const propias = await obtenerReservasUsuario(user.id);
          // Ordenar por creación si existe, sino por Fecha+Hora
          const toTs = (r) => {
            const creado = r.CreadoEn || r.creadoEn || null;
            if (creado) {
              const d = new Date(creado);
              if (!isNaN(d.getTime())) return d.getTime();
            }
            let base = String(r.Fecha || '').trim();
            let hora = String(r.Hora || '').trim();
            if (base && hora) {
              const dt = new Date(`${base}T${hora.length===5?hora+':00':hora}`);
              if (!isNaN(dt.getTime())) return dt.getTime();
            }
            return Number(r.Id || 0);
          };
          const sorted = [...propias].sort((a,b)=> toTs(b) - toTs(a));
          setReservas(sorted);
        }
      } catch (error) {
        notifyError('Error al cargar reservas: ' + error.message);
      }
    };
    if (user) cargarReservas();
  }, [user, notifyError]);

  if (!user) return null;

  const fmtFecha = (v) => toDDMMYYYY(v);
  const fmtHoraAmPm = (v) => to12hWithAmPm(extractHHmm(v));

  return (
    <div className="w-screen max-w-none -mx-[calc((100vw-100%)/2)] px-2 md:px-8 py-6">
      <h2 className="text-2xl font-bold mb-6 text-purple-700">Mis Reservas</h2>

      <div className="w-full max-w-none rounded-xl border border-purple-200 shadow-lg bg-white overflow-hidden hidden md:block">
        <table className="w-full table-auto divide-y divide-purple-100">
          <thead className="bg-purple-50">
            <tr>
              <th className="px-3 py-3 text-xs font-bold text-purple-700 text-left">N° Reserva</th>
              <th className="px-3 py-3 text-xs font-bold text-purple-700 text-left">Servicio</th>
              <th className="px-3 py-3 text-xs font-bold text-purple-700 text-left">Manicurista</th>
              <th className="px-3 py-3 text-xs font-bold text-purple-700 text-left">Fecha</th>
              <th className="px-3 py-3 text-xs font-bold text-purple-700 text-left">Hora</th>
              <th className="px-3 py-3 text-xs font-bold text-purple-700 text-left">Estado</th>
              <th className="px-3 py-3 text-xs font-bold text-purple-700 text-left">Notas</th>
              <th className="px-3 py-3 text-xs font-bold text-purple-700 text-left">Reagendamiento</th>
              <th className="px-3 py-3 text-xs font-bold text-purple-700 text-left">Calificación</th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-purple-100">
            {reservas.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE).map(reserva => (
              <tr key={reserva.Id} className="align-top">
                <td className="px-3 py-3 break-words">
                  <span className="inline-block px-2 py-1 rounded bg-indigo-50 text-indigo-700 text-xs font-semibold border border-indigo-200">
                    {reserva.NumeroReserva || reserva.numeroReserva || reserva.Id || '—'}
                  </span>
                </td>
                <td className="px-3 py-3 break-words">{reserva.NombreProducto || '—'}</td>
                <td className="px-3 py-3 break-words">{reserva.NombreManicurista || '-'}</td>
                <td className="px-3 py-3 break-words">{fmtFecha(reserva.Fecha)}</td>
                <td className="px-3 py-3 break-words">{fmtHoraAmPm(reserva.Hora)}</td>
                <td className="px-3 py-3">
                  <span
                    className={`inline-block px-2 py-1 rounded font-semibold text-xs ${
                      reserva.Estado === 'pendiente'
                        ? 'bg-yellow-100 text-yellow-800'
                        : reserva.Estado === 'confirmada'
                        ? 'bg-green-100 text-green-800'
                        : reserva.Estado === 'cancelada'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {reserva.Estado || '—'}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <button
                    className="px-2 py-1 rounded text-xs border border-purple-200 text-purple-700 hover:bg-purple-50 disabled:opacity-50"
                    disabled={!reserva.Notas}
                    onClick={()=>setNotaSel(reserva.Notas)}
                  >Ver nota</button>
                </td>
                <td className="px-3 py-3">
                  <button
                    className="px-2 py-1 rounded text-xs border border-purple-200 text-purple-700 hover:bg-purple-50 disabled:opacity-50"
                    disabled={!reserva.MensajeReagendamiento}
                    onClick={()=>setMensajeSel(reserva.MensajeReagendamiento)}
                  >Ver estado</button>
                </td>
                <td className="px-3 py-3">
                  {reserva.Calificacion ? (
                    <div className="flex items-center gap-1">
                      {Array.from({ length: reserva.Calificacion }).map((_, i) => (
                        <span key={i} className="text-yellow-400">★</span>
                      ))}
                      {Array.from({ length: 5 - reserva.Calificacion }).map((_, i) => (
                        <span key={i} className="text-gray-300">★</span>
                      ))}
                    </div>
                  ) : (
                    <button
                      className="px-2 py-1 rounded text-xs border border-purple-200 text-purple-700 hover:bg-purple-50"
                      onClick={() => { setRatingReservaSel(reserva); setRatingStars(0); setRatingComment(''); }}
                    >Evaluar</button>
                  )}
                </td>
              </tr>
            ))}

            {reservas.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-10 text-center text-purple-500 font-semibold">
                  No tienes reservas registradas.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* VISTA MÓVIL: Tarjetas */}
      <div className="md:hidden space-y-4">
        {reservas.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE).map(reserva => (
          <div key={reserva.Id} className="bg-white rounded-xl border border-purple-200 shadow-lg p-4 space-y-3">
            {/* Encabezado: N° y Estado */}
            <div className="flex justify-between items-start gap-2">
              <span className="inline-block px-2 py-1 rounded bg-indigo-50 text-indigo-700 text-xs font-semibold border border-indigo-200">
                {reserva.NumeroReserva || reserva.Id || '—'}
              </span>
              <span className={`inline-block px-2 py-1 rounded font-semibold text-xs ${
                reserva.Estado === 'pendiente'
                  ? 'bg-yellow-100 text-yellow-800'
                  : reserva.Estado === 'confirmada'
                  ? 'bg-green-100 text-green-800'
                  : reserva.Estado === 'cancelada'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-gray-100 text-gray-700'
              }`}>
                {reserva.Estado || '—'}
              </span>
            </div>

            {/* Datos principales */}
            <div className="space-y-2 text-sm">
              <div>
                <span className="font-semibold text-purple-700">Servicio:</span> {reserva.NombreProducto || '—'}
              </div>
              <div>
                <span className="font-semibold text-purple-700">Fecha/Hora:</span> {fmtFecha(reserva.Fecha)} {fmtHoraAmPm(reserva.Hora)}
              </div>
            </div>

            {/* Botones de información */}
            <div className="flex flex-col gap-2">
              {reserva.Notas && (
                <button
                  className="w-full px-2 py-1 rounded text-xs border border-purple-200 text-purple-700 hover:bg-purple-50"
                  onClick={()=>setNotaSel(reserva.Notas)}
                >
                  Ver nota
                </button>
              )}
              {reserva.MensajeReagendamiento && (
                <button
                  className="w-full px-2 py-1 rounded text-xs border border-purple-200 text-purple-700 hover:bg-purple-50"
                  onClick={()=>setMensajeSel(reserva.MensajeReagendamiento)}
                >
                  Ver reagendamiento
                </button>
              )}
            </div>

            {/* Calificación */}
            <div>
              <span className="font-semibold text-purple-700 text-sm">Calificación:</span>
              {reserva.Calificacion ? (
                <div className="flex items-center gap-1 mt-1">
                  {Array.from({ length: reserva.Calificacion }).map((_, i) => (
                    <span key={i} className="text-yellow-400">★</span>
                  ))}
                  {Array.from({ length: 5 - reserva.Calificacion }).map((_, i) => (
                    <span key={i} className="text-gray-300">★</span>
                  ))}
                </div>
              ) : (
                <button
                  className="w-full mt-1 px-2 py-1 rounded text-xs border border-purple-200 text-purple-700 hover:bg-purple-50"
                  onClick={() => { setRatingReservaSel(reserva); setRatingStars(0); setRatingComment(''); }}
                >
                  Evaluar servicio
                </button>
              )}
            </div>
          </div>
        ))}

        {reservas.length === 0 && (
          <div className="text-center py-10 text-purple-500 font-semibold">
            No tienes reservas registradas.
          </div>
        )}
      </div>

      {/* Paginación */}
      {reservas.length > PAGE_SIZE && (
        <div className="flex justify-center items-center gap-2 mt-4">
          <button disabled={page===1} onClick={()=>setPage(p=>Math.max(1,p-1))} className="px-3 py-1 rounded border bg-white disabled:opacity-50">Anterior</button>
          <span className="text-sm text-gray-600">Página {page} de {Math.ceil(reservas.length / PAGE_SIZE)}</span>
          <button disabled={page>=Math.ceil(reservas.length/PAGE_SIZE)} onClick={()=>setPage(p=>Math.min(Math.ceil(reservas.length/PAGE_SIZE),p+1))} className="px-3 py-1 rounded border bg-white disabled:opacity-50">Siguiente</button>
        </div>
      )}

      {mensajeSel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl border border-purple-200">
            <h3 className="text-lg font-bold text-purple-700 mb-3">Mensaje de reagendamiento</h3>
            <p className="text-gray-700 whitespace-pre-wrap">{mensajeSel}</p>
            <div className="text-right mt-4">
              <button onClick={()=>setMensajeSel(null)} className="px-4 py-2 rounded-xl bg-purple-600 text-white hover:bg-purple-700">Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {notaSel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl border border-purple-200">
            <h3 className="text-lg font-bold text-purple-700 mb-3">Nota de la Reserva</h3>
            <p className="text-gray-700 whitespace-pre-wrap">{notaSel}</p>
            <div className="text-right mt-4">
              <button onClick={()=>setNotaSel(null)} className="px-4 py-2 rounded-xl bg-purple-600 text-white hover:bg-purple-700">Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {ratingReservaSel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl border border-purple-200">
            <h3 className="text-lg font-bold text-purple-700 mb-3">Evaluar servicio</h3>
            <p className="text-sm text-gray-600 mb-3">Reserva N° <strong>{ratingReservaSel.NumeroReserva || ratingReservaSel.Id}</strong> — {ratingReservaSel.NombreProducto || 'Servicio'}</p>
            <div className="flex items-center gap-2 mb-4">
              {Array.from({ length: 5 }).map((_, i) => {
                const idx = i + 1;
                const active = ratingStars >= idx;
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setRatingStars(idx)}
                    className={active ? 'text-yellow-400 text-2xl' : 'text-gray-300 text-2xl'}
                  >★</button>
                );
              })}
            </div>
            <div>
              <label className="block text-xs font-semibold text-purple-700 mb-1">Comentario (opcional)</label>
              <textarea
                rows={3}
                value={ratingComment}
                onChange={(e) => setRatingComment(e.target.value)}
                placeholder="Escribe un comentario breve sobre tu experiencia"
                className="w-full border-2 border-purple-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              />
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setRatingReservaSel(null); setRatingStars(0); setRatingComment(''); }}
                className="px-4 py-2 rounded-xl bg-gray-200 text-gray-700 hover:bg-gray-300"
              >Cancelar</button>
              <button
                onClick={async () => {
                  if (ratingStars < 1) { notifyError('Selecciona entre 1 y 5 estrellas'); return; }
                  try {
                    setSavingRating(true);
                    await calificarReserva(ratingReservaSel.Id, { Estrellas: ratingStars, Comentario: ratingComment });
                    notifySuccess('¡Gracias por tu evaluación!');
                    setRatingReservaSel(null);
                    // refrescar lista
                    const propias = await obtenerReservasUsuario(user.id);
                    const toTs = (r) => {
                      const creado = r.CreadoEn || r.creadoEn || null;
                      if (creado) { const d = new Date(creado); if (!isNaN(d.getTime())) return d.getTime(); }
                      let base = String(r.Fecha || '').trim(); let hora = String(r.Hora || '').trim();
                      if (base && hora) { const dt = new Date(`${base}T${hora.length===5?hora+':00':hora}`); if (!isNaN(dt.getTime())) return dt.getTime(); }
                      return Number(r.Id || 0);
                    };
                    setReservas([...propias].sort((a,b)=> toTs(b) - toTs(a)));
                  } catch (e) {
                    notifyError('Error al enviar calificación: ' + e.message);
                  } finally {
                    setSavingRating(false);
                  }
                }}
                disabled={savingRating}
                className="px-4 py-2 rounded-xl bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
              >Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservasUsuarios;
